import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import closeButton from "../../../assets/images/close.png";
import ViewApproveWorkOrderDetailsModal from "../../../components/dashboard/Approvals/WorkOrder/ViewApproveWorkOrderDetailsModal";
import { approveWorkOrderAction, getWorkOrderApprovalList } from "../../../services/AppService";
import ApproveActionModal from "../../../components/dashboard/Approvals/ApprovalsPlanEstimate/ApproveActionModal";
import MessageModal from "../../../components/AdminComponents/Modals/MessageModal";
import { useTranslation } from "react-i18next";

const ApproveWorkOrder = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [approveModalOpen, setApproveModalOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);


    // Messaging system state
    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "success",
        title: "",
        message: ""
    });

    const isOfficer = user?.designation?.toLowerCase().includes("officer");

    useEffect(() => { fetchList(); }, [search, page, size]);

    const fetchList = async () => {
        try {
            setLoading(true);
            const res = await getWorkOrderApprovalList(search, page, size);
            setOrders(res.content || []);
            setTotalPages(res.totalPages || 0);
            setTotalElements(res.totalElements || 0);
        } catch (err) {
            console.error("Failed to fetch work orders", err);
        } finally { setLoading(false); }
    };

    const handleConfirm = async (id, remark) => {
        try {
            await approveWorkOrderAction(id, remark);
            setApproveModalOpen(false);
            setViewModalOpen(false);
            await fetchList();

            // Trigger success message
            setMessageModal({
                open: true,
                type: "success",
                title: "Success",
                message: "Work Order Approved Successfully"
            });
        } catch (err) {
            // Trigger error message
            setMessageModal({
                open: true,
                type: "error",
                title: "Error",
                message: err.response?.data?.message || "Approval process failed"
            });
        }
    };

    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">{t('approvals.headingApproveWorkOrder')}</h4>
                <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2 text-sm">
                    <label>{t('common.show')}</label>
                    <select
                        value={size}
                        onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                        className="border rounded px-2"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <label>{t('common.entries')}</label>
                </div>
                <input
                    type="text"
                    placeholder={t('common.search')}
                    className="border rounded px-3 py-1 text-sm"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                />
            </div>

            <div className="overflow-x-auto">

                <table className="w-full border text-sm ctable rounded-lg">
                    <thead className="bg-[#a0adf1] text-white">
                        <tr>
                            <th className="px-2 py-2 border border-black w-10">{t('tables.srNo')}</th>
                            <th className="px-2 py-2 border border-black text-left">{t('work.workName')}</th>
                            <th className="px-2 py-2 border border-black text-left">{t('work.tenderId')}</th>
                            <th className="px-2 py-2 border border-black text-left">{t('work.bidderName')}</th>
                            <th className="px-2 py-2 border border-black text-right">{t('work.bidAmount')}</th>
                            <th className="px-2 py-2 border border-black text-left">{t('work.workOrderNo')}</th>
                            <th className="px-2 py-2 border border-black text-center w-12">{t('work.duration')}</th>
                            <th className="px-2 py-2 border border-black text-center w-20">{t('work.orderDate')}</th>
                            <th className="px-2 py-2 border border-black text-center w-20">{t('work.startDate')}</th>
                            <th className="px-2 py-2 border border-black text-center w-20">{t('work.endDate')}</th>
                            <th className="px-2 py-2 border border-black text-center w-14">{t('tables.view')}</th>
                            <th className="px-2 py-2 border border-black text-center w-20">{t('approvals.approvalStatus')}</th>
                        </tr>
                    </thead>
                    <tbody>{loading ? (
                        <tr><td colSpan="12" className="text-center p-4">{t('common.loading')}</td></tr>
                    ) : orders.length === 0 ? (
                        <tr><td colSpan="12" className="text-center py-4">{t('common.noRecords')}</td></tr>
                    ) : (
                        orders.map((item, idx) => (
                            <tr key={item.id} className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-gray-100 transition-colors`}>
                                <td className="px-2 py-2 text-center border-r">{page * size + idx + 1}</td>
                                <td className="px-2 py-2 border-r font-medium uppercase">{item.workTitle}</td>
                                <td className="px-2 py-2 border-r">{item.tenderId}</td>
                                <td className="px-2 py-2 border-r">{item.bidderName}</td>
                                <td className="px-2 py-2 border-r text-right font-bold">
                                    {item.bidAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </td>
                                <td className="px-2 py-2 border-r font-semibold">{item.workOrderNo}</td>
                                <td className="px-2 py-2 text-center border-r">{item.duration}</td>
                                <td className="px-2 py-2 text-center border-r whitespace-nowrap">{item.workOrderDate}</td>
                                <td className="px-2 py-2 text-center border-r whitespace-nowrap">{item.workStartDate}</td>
                                <td className="px-2 py-2 text-center border-r whitespace-nowrap">{item.workEndDate}</td>
                                <td className="px-2 py-2 text-center border-r">
                                    <button className="btnImgView p-1 mx-auto" onClick={() => { setSelectedItem(item); setViewModalOpen(true); }}></button>
                                </td>
                                <td className="px-2 py-2 text-center">
                                    {item.isApproved ? (
                                        <span className="text-green-600 font-bold text-[10px] uppercase">{t('status.approved')}</span>
                                    ) : (
                                        <span className="text-yellow-600 font-bold text-[10px] uppercase">{t('status.pending')}</span>
                                    )}
                                </td>
                            </tr>
                        )
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4 border-t pt-4">
                <div className="text-sm text-gray-600">
                    {t('common.showing')} {orders.length > 0 ? page * size + 1 : 0} {t('common.to')} {Math.min((page + 1) * size, totalElements)} {t('common.of')} {totalElements} {t('common.entries')}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        className={`px-3 py-1 border rounded text-sm ${page === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-blue-600 hover:bg-gray-50'}`}
                        onClick={() => setPage(prev => Math.max(0, prev - 1))}
                        disabled={page === 0}
                    >
                        {t('common.previous')}
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            className={`px-3 py-1 border rounded text-sm ${page === i ? 'bg-[#a0adf1] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                            onClick={() => setPage(i)}
                        >
                            {i + 1}
                        </button>
                    )).slice(Math.max(0, page - 2), Math.min(totalPages, page + 3))}

                    <button
                        className={`px-3 py-1 border rounded text-sm ${page >= totalPages - 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-blue-600 hover:bg-gray-50'}`}
                        onClick={() => setPage(prev => Math.min(totalPages - 1, prev + 1))}
                        disabled={page >= totalPages - 1}
                    >
                        {t('common.next')}
                    </button>
                </div>
            </div>

            {/* Modals */}

            <ViewApproveWorkOrderDetailsModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                data={selectedItem}
                onApprove={(item) => { setSelectedItem(item); setApproveModalOpen(true); }}
            />

            <ApproveActionModal
                isOpen={approveModalOpen}
                onClose={() => setApproveModalOpen(false)}
                onConfirm={handleConfirm}
                estimate={selectedItem}
                title={t('approvals.doApproveWorkOrder')}
            />

            {/* Messaging System Integration */}
            <MessageModal
                open={messageModal.open}
                type={messageModal.type}
                title={messageModal.title}
                message={messageModal.message}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
            />
        </div>
    );
};

export default ApproveWorkOrder;